import { useState, useRef, useEffect, useCallback } from 'react'
import { get, post } from '../lib/api'
import * as ollama from '../lib/ollama'

/**
 * Voice of God — the admin command line.
 *
 * One input, plain instructions, and a junior developer on the other end who
 * does the safe obvious things and escalates the rest. It is always the last
 * thing on the screen and always focusable with `/`, because the whole point is
 * that acting on what you just read costs one keystroke rather than a trip to
 * a form.
 *
 * The reply is deliberately verbose about what it DID: "set to Complete, the
 * workbook still says Not Started" is the sentence that stops an override
 * quietly becoming a fact.
 */

type HelpDoc = { title: string; groups: Array<{ name: string; lines: string[][] }>; ids: string; truth: string }
type Reply = {
  ok: boolean; outcome: 'ok' | 'refused' | 'escalated' | 'error'; message: string
  ticketId?: string; lastRef?: string | null
  detail?: { help?: HelpDoc; entity?: string; ref?: string; row?: Record<string, unknown>
    overrides?: Array<{ field: string; value: string }>; notes?: Array<{ body: string; created_at: number }> }
  /** Set when Ollama turned prose into the command that actually ran. */
  translatedFrom?: string
}

const FIELD_LABEL: Record<string, string> = {
  task: 'Task', workstream: 'Workstream', owner: 'Owner', due_on: 'Due', status: 'Status',
  dependency: 'Depends on', evidence: 'Evidence', next_action: 'Next action', risk_if_late: 'Risk if late',
  name: 'Gate', pass_condition: 'Pass condition', if_not_passed: 'If not passed',
  risk: 'Risk', impact: 'Impact', likelihood: 'Likelihood', mitigation: 'Mitigation',
  where_to_fix: 'Where to fix', note: 'Note',
}

function RowAnswer({ detail }: { detail: NonNullable<Reply['detail']> }) {
  const row = detail.row ?? {}
  const overridden = new Set((detail.overrides ?? []).map((o) => o.field))
  const entries = Object.entries(row).filter(([k, v]) => k !== 'ref' && k !== 'sort' && v !== null && v !== '')
  return (
    <div className="vog-answer">
      <div className="vog-answer-head">{detail.ref}</div>
      <dl>
        {entries.map(([k, v]) => (
          <div key={k}>
            <dt>{FIELD_LABEL[k] ?? k}</dt>
            <dd>
              {String(v)}
              {overridden.has(k) && <span className="cc-ovr">set here</span>}
            </dd>
          </div>
        ))}
      </dl>
      {(detail.notes ?? []).length > 0 && (
        <div className="vog-notes">
          <strong>Notes</strong>
          {(detail.notes ?? []).map((n, i) => (
            <p key={i}>{n.body} <span className="cc-sub">{new Date(n.created_at).toLocaleDateString()}</span></p>
          ))}
        </div>
      )}
    </div>
  )
}

function HelpPanel({ help }: { help: HelpDoc }) {
  return (
    <div className="vog-help">
      {help.groups.map((g) => (
        <div key={g.name} className="vog-help-group">
          <strong>{g.name}</strong>
          <dl>
            {g.lines.map(([cmd, what]) => (
              <div key={cmd}><dt><code>{cmd}</code></dt><dd>{what}</dd></div>
            ))}
          </dl>
        </div>
      ))}
      <p className="cc-sub">{help.ids}</p>
      <p className="cc-sub">{help.truth}</p>
    </div>
  )
}

const EXAMPLES = ['help', 'tell me about C01', 'C01 done', 'G1 passed', 'fico 696', 'ticket order the survey']

export default function VoiceOfGod({ onChanged }: { onChanged: () => void }) {
  const [command, setCommand] = useState('')
  const [busy, setBusy] = useState(false)
  const [replies, setReplies] = useState<Array<Reply & { raw: string }>>([])
  // The row the conversation is currently about, so "it done" works after
  // "tell me about C02". Held on the client: a server-side conversation would
  // be one more thing to expire and get out of step with what is on screen.
  const [lastRef, setLastRef] = useState<string | null>(null)
  const [llm, setLlm] = useState<ollama.Status>({ up: false, models: [] })
  const [vocab, setVocab] = useState<ollama.Vocabulary | null>(null)
  const [thinking, setThinking] = useState(false)
  // What the agent proposes to do, waiting to be confirmed. Reads never land
  // here — only the things that write.
  const [pending, setPending] = useState<ollama.Proposal | null>(null)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // "/" focuses the command line from anywhere, unless you are already typing.
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')
      if (e.key === '/' && !typing) {
        e.preventDefault()
        input.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    void ollama.status().then(setLlm)
    void get<ollama.Vocabulary>('/api/voice/vocabulary').then(setVocab).catch(() => setVocab(null))
  }, [])

  const execute = useCallback(async (raw: string, translatedFrom?: string) => {
    const reply = await post<Reply>('/api/voice', { command: raw, lastRef })
    setReplies((r) => [{ ...reply, raw, translatedFrom }, ...r].slice(0, 6))
    if (reply.lastRef !== undefined) setLastRef(reply.lastRef)
    // Refresh whatever the command changed, so the screen never disagrees with
    // the thing it just told you it did.
    onChanged()
  }, [lastRef, onChanged])

  async function send(e: React.FormEvent) {
    e.preventDefault()
    const raw = command.trim()
    if (!raw || busy) return
    setBusy(true)
    try {
      /*
       * Ask the grammar first, without writing anything.
       *
       * A dry run is what makes the model optional: if the sentence already
       * parses, nothing is translated and nothing is slower. Only the input
       * that would have become a ticket goes to Ollama.
       */
      const dry = llm.up && vocab
        ? await post<Reply>('/api/voice', { command: raw, lastRef, dryRun: true })
        : { ok: true } as Reply

      if (dry.ok) {
        // Already a command. Nothing to interpret, nothing to confirm.
        await execute(raw)
        setCommand('')
        return
      }

      /*
       * Prose. Ask the agent what it thinks this is.
       *
       * A read happens at once — showing a row changes nothing, and asking to
       * confirm a question would be theatre. Anything that WRITES is proposed
       * and waits: the operator sees the row it chose and the change it means
       * before it happens, which is the only defence against it choosing the
       * wrong row.
       */
      setThinking(true)
      const proposal = await ollama.interpret(vocab!, raw)
      setThinking(false)

      if (!proposal) {
        await execute(raw)
        setCommand('')
        return
      }

      // The proposal is checked against the grammar before it is offered, so an
      // invented row id never reaches a confirm card.
      const check = await post<Reply>('/api/voice', { command: proposal.command, lastRef, dryRun: true })
      if (!check.ok) {
        await execute(raw)
        setCommand('')
        return
      }

      if (proposal.immediate) {
        await execute(proposal.command, raw)
        setCommand('')
      } else {
        setPending(proposal)
      }
    } catch (err) {
      const failure: Reply & { raw: string } = {
        ok: false, outcome: 'error', message: err instanceof Error ? err.message : 'Failed.', raw,
      }
      setReplies((r) => [failure, ...r].slice(0, 6))
    } finally {
      setBusy(false)
      setThinking(false)
    }
  }

  return (
    <section className="vog" aria-label="Voice of God">
      <div className="vog-head">
        <h2>Voice of God</h2>
        <span className="muted vog-hint">
          press <kbd>/</kbd> to focus · type <button type="button" className="link" onClick={() => { setCommand('help'); input.current?.focus() }}>help</button>
          {lastRef && <> · talking about <strong>{lastRef}</strong></>}
          {' · '}
          <span title={llm.up ? `${ollama.model()} on this machine` : (llm.reason ?? 'not running')}>
            {llm.up ? `plain English via ${ollama.model().split(':')[0]}` : 'grammar only'}
          </span>
        </span>
      </div>

      <form onSubmit={send} className="vog-form">
        <input
          ref={input}
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          placeholder={lastRef ? `ask about ${lastRef}, or "it done" · type help` : 'tell me about C01 · C01 done · help'}
          aria-label="Command"
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
        />
        <button type="submit" disabled={busy || !command.trim()}>{thinking ? 'reading…' : busy ? '…' : 'Do it'}</button>
      </form>

      {pending && (
        <div className="vog-confirm" role="alertdialog" aria-label="Confirm this change">
          <div className="vog-confirm-what">
            <strong>{pending.says}</strong>
            <code>{pending.command}</code>
          </div>
          <div className="vog-confirm-do">
            <button
              type="button"
              onClick={async () => {
                const p = pending
                setPending(null)
                setBusy(true)
                try { await execute(p.command, command.trim()); setCommand('') } finally { setBusy(false) }
              }}
            >
              Do it
            </button>
            <button type="button" className="link" onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      )}

      {replies.length === 0 && !pending && (
        <div className="vog-examples">
          {EXAMPLES.map((x) => (
            <button key={x} type="button" className="vog-chip" onClick={() => { setCommand(x); input.current?.focus() }}>
              {x}
            </button>
          ))}
        </div>
      )}

      {replies.length > 0 && (
        <ul className="vog-log">
          {replies.map((r, i) => (
            <li key={i} className={`vog-reply vog-${r.outcome}`}>
              {r.translatedFrom && <span className="vog-from">you said: {r.translatedFrom}</span>}
              <code>{r.raw}</code>
              <span>{r.message}</span>
              {r.detail?.help && <HelpPanel help={r.detail.help} />}
              {r.detail?.row && <RowAnswer detail={r.detail} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
